import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";

import useSeo from "@/hooks/useSeo";
import useBookingForm, { toISODate } from "@/hooks/useBookingForm";
import { formatApiError } from "@/lib/api";
import { createBooking, createCheckoutSession } from "@/services/booking";

import BookingStepper from "@/components/booking/BookingStepper";
import StepPackage from "@/components/booking/StepPackage";
import StepWhen from "@/components/booking/StepWhen";
import StepDetails from "@/components/booking/StepDetails";
import StepReview from "@/components/booking/StepReview";
import StepConfirmation from "@/components/booking/StepConfirmation";

const DEPOSIT = 49;

export default function Booking() {
  useSeo({
    title: "Book Online | Full Indoor Air Services",
    description: "Book your air duct or dryer vent cleaning in 60 seconds. Same-week appointments across MD, DC, and VA.",
    canonical: "https://capitalindoorair.com/book",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const presetPackage = params.get("package");
  const presetReferral = params.get("ref");

  const f = useBookingForm(presetPackage, presetReferral);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...f.form,
        package_id: f.packageId,
        add_ons: f.selectedAddOns,
        preferred_date: toISODate(f.date),
        time_slot: f.timeSlot,
        referral_code: f.referralCode || undefined,
      };
      const data = await createBooking(payload);
      setConfirmation(data);

      if (f.payDeposit) {
        try {
          const checkout = await createCheckoutSession(data.booking.id, window.location.origin);
          window.sessionStorage.setItem(
            "fia_pending_booking",
            JSON.stringify({
              confirmation_number: data.confirmation_number,
              email: f.form.email,
              referral_code: data.referral_code,
              referral_credit: data.referral_credit,
            })
          );
          window.location.href = checkout.url;
          return;
        } catch (e) {
          console.error("Stripe checkout failed", e);
        }
      }
      f.setStep(5);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-bone min-h-[80vh]" data-testid="booking-page">
      <section className="section-pad">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <div className="eyebrow mb-3">Book online</div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tighter font-light">
              Three quick steps to <span className="text-forest">fresh indoor air.</span>
            </h1>
          </div>

          {f.step < 5 && <BookingStepper step={f.step} />}

          {f.step === 1 && (
            <StepPackage
              packages={f.packages}
              addOns={f.addOns}
              packageId={f.packageId}
              setPackageId={f.setPackageId}
              selectedAddOns={f.selectedAddOns}
              setSelectedAddOns={f.setSelectedAddOns}
            />
          )}

          {f.step === 2 && (
            <StepWhen
              dates={f.dates}
              date={f.date}
              setDate={f.setDate}
              slots={f.slots}
              timeSlot={f.timeSlot}
              setTimeSlot={f.setTimeSlot}
            />
          )}

          {f.step === 3 && (
            <StepDetails
              form={f.form}
              setForm={f.setForm}
              referralCode={f.referralCode}
              setReferralCode={f.setReferralCode}
            />
          )}

          {f.step === 4 && (
            <StepReview
              selectedPackage={f.selectedPackage}
              selectedAddOns={f.selectedAddOns}
              addOns={f.addOns}
              date={f.date}
              timeSlot={f.timeSlot}
              form={f.form}
              total={f.total}
              payDeposit={f.payDeposit}
              setPayDeposit={f.setPayDeposit}
              deposit={DEPOSIT}
              error={error}
            />
          )}

          {f.step === 5 && confirmation && (
            <StepConfirmation
              confirmation={confirmation}
              email={f.form.email}
              onHome={() => navigate("/")}
            />
          )}

          {/* Nav buttons */}
          {f.step < 5 && (
            <div className="flex items-center justify-between mt-12">
              <button
                onClick={() => f.setStep(Math.max(1, f.step - 1))}
                disabled={f.step === 1}
                className="btn-ghost disabled:opacity-40"
                data-testid="step-back"
              >
                <ArrowLeft size={14} /> Back
              </button>
              {f.step < 4 ? (
                <button
                  onClick={() => f.setStep(f.step + 1)}
                  disabled={!f.canNext()}
                  className="btn-primary disabled:opacity-50"
                  data-testid="step-next"
                >
                  Continue <ArrowRight size={14} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60"
                  data-testid="confirm-booking"
                >
                  {submitting
                    ? (f.payDeposit ? "Redirecting to checkout…" : "Booking…")
                    : (f.payDeposit ? `Confirm & Pay $${DEPOSIT}` : "Confirm Booking")
                  } <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
